import {
  addBrandingThemeService,
  copyBrandingThemeService,
  deleteBrandingThemeService,
  editBrandingThemeService,
  getBrandingThemePresignedUrlService,
  getBrandingThemesDetailsService,
  getBrandingThemesListService,
  updateDefaultBrandingThemeService,
} from '@/services/brandingThemes';
import {
  BrandingThemeAddSubmission,
  BrandingThemeEditSubmission,
  queryKeys,
} from '@/types/brandingThemes';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
}

export const useGetBrandingThemesList = (queryParams?: {
  level?: 'All' | 'Program' | 'Company';
  company_id?: string;
}) => {
  const params = new URLSearchParams(queryParams).toString();

  const { data, isPending } = useQuery({
    queryFn: () => getBrandingThemesListService(params),
    queryKey: [queryKeys.list, params],
  });

  return {
    brandingThemes: data,
    isBrandingThemesPending: isPending,
  };
};

export const useGetBrandingThemeDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryFn: () => getBrandingThemesDetailsService(uuid),
    queryKey: [queryKeys.list, uuid],
    enabled: !!uuid,
  });

  return {
    brandingTheme: data,
    isBrandingThemePending: isPending,
  };
};

export function useUpdateDefaultBrandingTheme() {
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateDefaultBrandingTheme,
    isPending: isUpdateDefaultBrandingThemePending,
  } = useMutation({
    mutationFn: (uuid: string) => updateDefaultBrandingThemeService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    updateDefaultBrandingTheme,
    isUpdateDefaultBrandingThemePending,
  };
}

export function useDeleteBrandingTheme() {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteBrandingTheme,
    isPending: isDeleteBrandingThemePending,
  } = useMutation({
    mutationFn: (uuid: string) => deleteBrandingThemeService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    deleteBrandingTheme,
    isDeleteBrandingThemePending,
  };
}

export function useCopyBrandingTheme() {
  const queryClient = useQueryClient();
  const {
    mutateAsync: copyBrandingTheme,
    isPending: isCopyBrandingThemePending,
  } = useMutation({
    mutationFn: (uuid: string) => copyBrandingThemeService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    copyBrandingTheme,
    isCopyBrandingThemePending,
  };
}

export function useAddBrandingTheme() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const {
    mutateAsync: addBrandingTheme,
    isPending: isAddBrandingThemePending,
  } = useMutation({
    mutationFn: (data: BrandingThemeAddSubmission) =>
      addBrandingThemeService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addBrandingTheme,
    isAddBrandingThemePending,
  };
}

export function useEditBrandingTheme() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const {
    mutateAsync: editBrandingTheme,
    isPending: isEditBrandingThemePending,
  } = useMutation({
    mutationFn: (data: BrandingThemeEditSubmission) =>
      editBrandingThemeService(data.uuid, { ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    editBrandingTheme,
    isEditBrandingThemePending,
  };
}

export const useGetBrandingThemePresignedUrl = () => {
  const {
    mutateAsync: getBrandingThemePresignedUrl,
    isPending: isGetBrandingThemePresignedUrlPending,
  } = useMutation({
    mutationFn: (data: { file: string; file_extension: string }) =>
      getBrandingThemePresignedUrlService(data.file, data.file_extension),
  });

  return {
    getBrandingThemePresignedUrl,
    isGetBrandingThemePresignedUrlPending,
  };
};
